// src/modules/demo/hooks/useDemoState.ts

import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import type { DemoState } from '../types';
import { createDemoFixtures } from '../data/demoFixtures';

const STORAGE_KEY = 'tutorify_demo_state';

function initializeDemoState(): DemoState {
  const baseDate = dayjs().format('YYYY-MM-DD');
  const fixtures = createDemoFixtures(baseDate);

  return {
    ...fixtures,
    baseDate,
    toastsShown: [],
    analyticsEmitted: [],
    initialized: true,
  };
}

function readFromStorage(): DemoState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoState;
    return parsed.initialized ? parsed : null;
  } catch {
    return null;
  }
}

function writeToStorage(state: DemoState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable — silent fail
  }
}

export function useDemoState() {
  const [state, setState] = useState<DemoState>(() => {
    const existing = readFromStorage();
    if (existing) return existing;
    const fresh = initializeDemoState();
    writeToStorage(fresh);
    return fresh;
  });

  const updateState = useCallback((updater: (prev: DemoState) => DemoState) => {
    setState((prev) => {
      const next = updater(prev);
      writeToStorage(next);
      return next;
    });
  }, []);

  const resetState = useCallback(() => {
    const fresh = initializeDemoState();
    writeToStorage(fresh);
    setState(fresh);
  }, []);

  return { state, updateState, resetState };
}
